import { InfoCircleOutlined } from '@ant-design/icons';
import WarningOutlined from '@ant-design/icons/WarningOutlined';
import App from 'antd/lib/app';
import Button from 'antd/lib/button';
import Col from 'antd/lib/col';
import DatePicker from 'antd/lib/date-picker';
import Form from 'antd/lib/form';
import Modal from 'antd/lib/modal/Modal';
import Row from 'antd/lib/row';
import Space from 'antd/lib/space';
import Tooltip from 'antd/lib/tooltip';
import Typography from 'antd/lib/typography';
import { theme } from 'antd/lib';
import InputNumber from 'antd/lib/input-number';
import { useEffect, useMemo, useState } from 'react';

import { FORM_ACTION } from '../../eventInterface';
import { IStandbyPeriod } from '../../../../domain/standby-period/interface';
import { useAuth } from '../../../../domain/auth/useAuth';
import { toBatchRequest, toViewStandbyPeriod } from './utils';
import { UserType } from '../../../../domain/user/interface';
import { ResourceCollapsedList } from '../ResourceCollapsedList/ResourceCollapsedList';
import { ProgramSelector } from '../../../../components/selectors/ProgramSelector/ProgramSelector';
import { EventConfirmationModal } from '../EventConfirmationModal';
import { StandbyPeriodConfirmationModalContent } from './StandbyPeriodConfirmantionModalContent';
import { isPastDate } from '../../utils';
import { CalendarEventPreview } from '../CalendarEventPreview';
import { createStandbyPeriodBatchByProgram } from '../../../../domain/standby-period';
import { useResourceListQuery } from '../../../../domain/event/queries';

export const StandbyPeriodByProgramFormModal = (props: { standbyPeriod: Partial<IStandbyPeriod>; onClose: Function }) => {
    const { token: themeColors } = theme.useToken();

    const { notification } = App.useApp();
    const [form] = Form.useForm();
    const auth = useAuth();

    const { standbyPeriod, onClose } = props;

    const [loading, setLoading] = useState<boolean>(false);
    const viewStandbyPeriod = useMemo(() => toViewStandbyPeriod(standbyPeriod), [standbyPeriod]);
    const [programName, setProgramName] = useState<string>('');

    const [formAction, setFormAction] = useState<FORM_ACTION | null>(null);
    const [formValid, setFormValid] = useState<boolean>(false);

    const datetimeStart = Form.useWatch('datetime_start_local', form);
    const durationHours = Form.useWatch('duration_hours', form);
    const programId = Form.useWatch('program_id', form);

    const { data, isLoading: isLoadingSites, isError, error } = useResourceListQuery({ programId: programId }, { enabled: !!programId });
    const sites = useMemo(() => data || [], [data]);

    useEffect(() => {
        (async () => {
            if (!datetimeStart || !durationHours || !programId) {
                setFormValid(false);
                return;
            }

            try {
                await form.validateFields(); // triggers all validators
                setFormValid(true);
            } catch {
                setFormValid(false); // if any validator fails
            }
        })();
    }, [datetimeStart, durationHours, programId, form]);

    const handleProgramSelect = (value: number | string, option: any) => {
        setProgramName(option.label);
    };

    //create standby period by program
    const createStandbyPeriodWithConfirmation = () => setFormAction(FORM_ACTION.CREATE);

    const createStandbyPeriodByProgramInBatchAction = async () => {
        setLoading(true);
        try {
            const preparedStandbyPeriod = await form.validateFields();
            const { standbyPeriod } = toBatchRequest(preparedStandbyPeriod);
            await createStandbyPeriodBatchByProgram(standbyPeriod, programId);
            notification.info({ key: 'standby-period-batch-save-success', message: 'Standby Periods saved!' });
            onClose(preparedStandbyPeriod);
        } catch (err: any) {
            notification.error({ key: 'standby-period-batch-save-error', message: err.message || 'Cannot save Standby Periods!' });
        }
        setLoading(false);
    };

    const closeStandbyPeriodConfirmationModal = () => setFormAction(null);

    const closeDialog = () => onClose();

    return (
        <Modal
            open
            destroyOnHidden
            title={'New Standby Period by Program'}
            onCancel={closeDialog}
            footer={[
                <Space wrap style={{ justifyContent: 'flex-end' }}>
                    <Button key="standby-period-by-program-form-close" data-cy="close-dialog" onClick={closeDialog}>
                        Close
                    </Button>

                    <Button
                        key="create-standby-period-by-program-action"
                        type="primary"
                        loading={loading}
                        onClick={createStandbyPeriodWithConfirmation}
                        disabled={!formValid}
                    >
                        Save ({sites.length})
                    </Button>
                </Space>,
            ]}
            className="standby-period-modal"
            data-cy="standby-period-by-program"
        >
            <div className="standby-period-form-container content-background" data-cy="warning-msg">
                <Form form={form} name="standby-period-by-program-form" preserve={false} layout="vertical" initialValues={viewStandbyPeriod}>
                    <Row>
                        <Col xs={24}>
                            {!!programId && (
                                <Form.Item
                                    name="resources"
                                    label={<Typography.Text strong>Resources</Typography.Text>}
                                    hasFeedback
                                    wrapperCol={{ span: 24 }}
                                >
                                    <ResourceCollapsedList resources={sites} eventDatetime={datetimeStart} loading={isLoadingSites} showTimezone />
                                </Form.Item>
                            )}

                            <Form.Item style={{ marginBottom: 0 }}>
                                <Space>
                                    <Form.Item
                                        name="datetime_start_local"
                                        required
                                        rules={[{ required: true, message: 'Please select date!' }]}
                                        label={
                                            <>
                                                <Typography.Text strong>Start time&nbsp;</Typography.Text>
                                                <Tooltip title="Standby Period will be dispatched to start at the site's local timezone">
                                                    <InfoCircleOutlined style={{ color: themeColors.colorPrimary }} />
                                                </Tooltip>
                                            </>
                                        }
                                    >
                                        <DatePicker data-cy="date-picker" format="DD/MMM/YYYY HH:mm" showTime={{ format: 'HH:mm' }} size="large" />
                                    </Form.Item>
                                    <Form.Item dependencies={['datetime_start_local']} style={{ marginBottom: 0 }}>
                                        {({ getFieldValue }) => {
                                            const selectedDate = getFieldValue('datetime_start_local');

                                            return (
                                                selectedDate &&
                                                isPastDate(getFieldValue('datetime_start_local')) && (
                                                    <Typography.Text type="secondary">
                                                        <WarningOutlined style={{ color: '#faad14' }} /> Standby Period started in the past.
                                                    </Typography.Text>
                                                )
                                            );
                                        }}
                                    </Form.Item>
                                </Space>
                            </Form.Item>

                            <Form.Item style={{ marginBottom: 0 }}>
                                <Space>
                                    <Form.Item
                                        label={
                                            <>
                                                <Typography.Text strong>Duration</Typography.Text>{' '}
                                            </>
                                        }
                                    >
                                        <Space>
                                            <Form.Item name="duration_hours" noStyle>
                                                <InputNumber data-cy="duration-hours" min={0} max={48} step={0.25} size="large" />
                                            </Form.Item>
                                            <Typography.Text>hours</Typography.Text>
                                        </Space>
                                    </Form.Item>
                                </Space>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={24}>
                            <Form.Item
                                required
                                label={<Typography.Text strong>Program</Typography.Text>}
                                name="program_id"
                                rules={[{ required: true, message: 'Please select program!' }]}
                            >
                                <ProgramSelector
                                    allowClear
                                    handleProgram={() => null}
                                    onSelect={handleProgramSelect}
                                    selectedProgram={programId}
                                    excludeUtilityCustomerPrograms={
                                        auth?.user?.user_type === UserType.CUSTOMER || auth?.user?.user_type === UserType.OPERATOR
                                    }
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <CalendarEventPreview
                        isStandbyPeriod={true}
                        preEventDurationHours={0}
                        eventDurationHours={durationHours}
                        postEventDurationHours={0}
                        secondaryPreEventDurationHours={0}
                    />
                </Form>

                {formAction && (
                    <EventConfirmationModal
                        action={formAction}
                        handleConfirm={createStandbyPeriodByProgramInBatchAction}
                        handleClose={closeStandbyPeriodConfirmationModal}
                        modalContent={
                            <StandbyPeriodConfirmationModalContent
                                standbyPeriod={{
                                    ...form.getFieldsValue(),
                                }}
                                affectedSites={sites}
                                actionType={'create:batchByProgram'}
                                programName={programName}
                            />
                        }
                    />
                )}
            </div>
        </Modal>
    );
};
